import type { ChangeEmitter, Store } from "@/data/repositories";
import type { Clock } from "@/domain/clock";
import { todayKey } from "@/domain/dates";
import { pickText } from "@/domain/localized";
import { TODAY_TOKEN } from "@/domain/pushMessage";
import { detectLocale } from "@/app/i18n/detect";
import { strings } from "@/app/i18n/strings";
import { newId } from "@/domain/ids";
import type { Api, ReminderSpec } from "./ApiClient";
import type { PushPlatform } from "./PushPlatform";

/**
 * unsupported: no push in this browser · needs-install: iOS outside the installed
 * app · prompt: never asked · denied: refused in the browser · granted: allowed
 * but not registered here · enabled: registered, reminders flow · paused:
 * registered, but the user switched reminders off; nothing is ever unsubscribed.
 */
export type ReminderState = "unsupported" | "needs-install" | "prompt" | "denied" | "granted" | "enabled" | "paused";

/** How long one step of enable() may stay silent before it is reported as stuck. */
export const STEP_TIMEOUT_MS = 20_000;
const PERMISSION_POLL_MS = 500;

interface Deps {
  api: Api;
  store: Store;
  clock: Clock;
  platform: PushPlatform;
  changeSources: readonly ChangeEmitter[];
  language?: string;
}

/** Turns "remind me at 21:00" per scenario into a browser push subscription the server can address. */
export class ReminderService {
  /** Why the last enable() did not end in "enabled"; shown in Settings so a failure is never silent. */
  lastError: string | null = null;
  private publicKey: Promise<string> | null = null;

  constructor(private readonly deps: Deps) {}

  /** Fetches the server key ahead of the tap: WebKit lets `subscribe` run only within a short user gesture. */
  prepare(): Promise<string> {
    this.publicKey ??= this.deps.api.pushPublicKey().catch((error: unknown) => {
      this.publicKey = null;
      throw error;
    });
    return this.publicKey;
  }

  async state(): Promise<ReminderState> {
    const { platform } = this.deps;
    if (!platform.supported()) return platform.isIOS() && !platform.isStandalone() ? "needs-install" : "unsupported";
    const permission = platform.permission();
    if (permission === "denied") return "denied";
    if (permission === "default") return "prompt";
    const settings = await this.deps.store.settings.get();
    if (!settings.push || (await platform.current())?.endpoint !== settings.push.endpoint) return "granted";
    return settings.push.paused ? "paused" : "enabled";
  }

  /**
   * Asks the browser, registers the subscription and sends the current reminders.
   * Every step is named and bounded: a browser that never answers (iOS has been
   * seen to hang here) ends in `lastError` naming the step, and the answer is the
   * state as it stands, so the section offers another tap instead of a spinner.
   */
  async enable(): Promise<ReminderState> {
    const { platform } = this.deps;
    this.lastError = null;
    if ((await this.state()) === "paused") {
      const settings = await this.deps.store.settings.get();
      await this.deps.store.settings.update({ push: { endpoint: settings.push!.endpoint } });
      await this.sync();
      return "enabled";
    }
    const keyRequest = this.prepare();
    try {
      if (platform.permission() !== "granted") {
        const answer = await this.step("permission", this.askPermission(), null);
        if (answer !== "granted") return this.state();
      }
      await this.step("service worker", platform.ready());
      let subscription = await this.step("subscription", platform.current());
      subscription ??= await this.step("subscription", platform.subscribe(await this.step("server key", keyRequest)));
      const settings = await this.deps.store.settings.get();
      const deviceId = settings.deviceId ?? newId();
      await this.step("server", this.deps.api.pushSubscribe({ endpoint: subscription.endpoint, keys: subscription.keys, timezone: platform.timezone(), deviceId }));
      await this.deps.store.settings.update({ push: { endpoint: subscription.endpoint }, deviceId });
    } catch (error) {
      this.lastError = describe(error);
      return this.state();
    }
    await this.sync();
    return "enabled";
  }

  /**
   * The browser's dialog has no time limit, but its promise is not trusted alone:
   * the permission value itself is polled, so a dialog that was answered while
   * the promise never settled still moves on.
   */
  private askPermission(): Promise<NotificationPermission> {
    const { platform } = this.deps;
    let timer: ReturnType<typeof setInterval> | undefined;
    const polled = new Promise<NotificationPermission>((resolve) => {
      timer = setInterval(() => {
        if (platform.permission() !== "default") resolve(platform.permission());
      }, PERMISSION_POLL_MS);
    });
    return Promise.race([platform.requestPermission(), polled]).finally(() => clearInterval(timer));
  }

  /** Runs one step of enable(); a failure or a silence longer than `timeoutMs` becomes an error that names the step. */
  private async step<T>(name: string, work: Promise<T>, timeoutMs: number | null = STEP_TIMEOUT_MS): Promise<T> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const silence = new Promise<never>((_, reject) => {
      if (timeoutMs !== null) timer = setTimeout(() => reject(new Error(`no answer in ${timeoutMs / 1000} s`)), timeoutMs);
    });
    try {
      return await Promise.race([work, silence]);
    } catch (error) {
      throw new Error(`${name}: ${describe(error)}`, { cause: error });
    } finally {
      clearTimeout(timer);
    }
  }

  /** Switches reminders off while keeping the browser subscription and the server registration. */
  async pause(): Promise<void> {
    const settings = await this.deps.store.settings.get();
    if (!settings.push) return;
    await this.deps.store.settings.update({ push: { endpoint: settings.push.endpoint, paused: true } });
    await this.sync();
  }

  /** Sends the server one reminder per enabled scenario, in the user's language; an empty list while paused. */
  async sync(): Promise<void> {
    const settings = await this.deps.store.settings.get();
    if (!settings.push) return;
    if (settings.push.paused) {
      await this.deps.api.pushReminders(settings.push.endpoint, []).catch(() => undefined);
      return;
    }
    const locale = settings.locale ?? detectLocale(this.deps.language);
    const t = strings[locale];
    const [subscriptions, scenarios] = await Promise.all([this.deps.store.subscriptions.listAll(), this.deps.store.scenarios.list()]);
    const reminders: ReminderSpec[] = subscriptions
      .filter((s) => s.enabled)
      .map((s) => {
        const scenario = scenarios.find((x) => x.id === s.scenarioId);
        return {
          key: s.id,
          time: s.reminderTime,
          title: scenario ? pickText(scenario.name, locale) : t.appName,
          body: t.reminders.pushBody,
          url: `/answer/${s.id}/${TODAY_TOKEN}`,
        };
      });
    await this.deps.api.pushReminders(settings.push.endpoint, reminders).catch(() => undefined);
  }

  /** Today's entry exists or the day was skipped: no reminder needed today. */
  /** Web push is sent by the server; the browser cannot list it. */
  async upcoming(): Promise<null> {
    return null;
  }

  async markDone(subscriptionId: string): Promise<void> {
    const settings = await this.deps.store.settings.get();
    if (!settings.push) return;
    await this.deps.api.pushDone(settings.push.endpoint, subscriptionId, todayKey(this.deps.clock)).catch(() => undefined);
  }

  /** Re-sends reminders whenever scenarios or their times change. */
  start(): () => void {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => void this.sync(), 1500);
    };
    const unsubscribes = this.deps.changeSources.map((source) => source.subscribe(schedule));
    void this.sync();
    return () => {
      for (const u of unsubscribes) u();
      if (timer) clearTimeout(timer);
    };
  }
}

function describe(error: unknown): string {
  if (error instanceof Error) return error.name && error.name !== "Error" ? `${error.name}: ${error.message}` : error.message;
  return String(error);
}
